import { type Money, type Odds, type SignedMoney } from '@urbanrats/contracts';
import { atomicOf, money, sameUnit, signedMoney } from './money.ts';

export interface PayoutInput {
  stake: Money;
  odds: Odds;
  lightning?: Odds | null;
  /** Sum of eligible boosts in basis points. */
  boostBps?: number;
  cap?: Money | null;
}

export interface PayoutResult {
  /** Total return including stake. */
  gross: Money;
  /** gross - stake; may be negative only if odds < 1. */
  net: SignedMoney;
  capped: boolean;
}

/**
 * gross = floor(stake × oddsNum × lightningNum × (10000 + boostBps) / (oddsDen × lightningDen × 10000));
 * payout = min(gross, cap). Single final rounding (floor), all integer arithmetic.
 */
export function computeWinPayout(input: PayoutInput): PayoutResult {
  const { stake, odds } = input;
  const lightning = input.lightning ?? { numerator: '1', denominator: '1' };
  const boost = input.boostBps ?? 0;
  if (!Number.isSafeInteger(boost) || boost < 0) throw new RangeError('boostBps must be a non-negative integer');
  const num = atomicOf(stake) * BigInt(odds.numerator) * BigInt(lightning.numerator) * BigInt(10000 + boost);
  const den = BigInt(odds.denominator) * BigInt(lightning.denominator) * 10000n;
  let gross = num / den; // BigInt division truncates toward zero == floor for non-negatives
  let capped = false;
  if (input.cap) {
    if (!sameUnit(input.cap, stake)) throw new RangeError('cap currency/scale must match stake');
    const cap = atomicOf(input.cap);
    if (gross > cap) {
      gross = cap;
      capped = true;
    }
  }
  return {
    gross: money(gross, stake.currency, stake.scale),
    net: signedMoney(gross - atomicOf(stake), stake.currency, stake.scale),
    capped,
  };
}

export function lossPayout(stake: Money): PayoutResult {
  return {
    gross: money(0n, stake.currency, stake.scale),
    net: signedMoney(-atomicOf(stake), stake.currency, stake.scale),
    capped: false,
  };
}
